import type { ComponentType } from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik as FormikBase } from "formik";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";

import { MSActivationBriefFormValidationSchema } from "@/helpers/validations";
import MSActivationBriefForm from "./MSActivationBriefForm";
import { morganStanleyService } from "@/features/morgan-stanley/services/morganStanleyService";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { MSPackagesData } from "@/features/morgan-stanley/helpers/staticData";
import { RpConfirmDialog } from "@/helpers/utils";
import RpConfirm from "@/components/RpConfirm";
import { locationService } from "@/features/locations/services/locationService";
import RpSnackbar from "@/components/alerts/RpSnackbar";
import { getUserThunk } from "@/features/profile/store/userSlice";
import { logger } from "@/helpers/loggerHelper";
import { HTTP_STATUS, USER_ROLE } from "@/config/constants";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";

const Formik = FormikBase as ComponentType<any>;

export default function MSActivationBrief() {
  const navigate = useNavigate();
  const { locID } = useParams();
  const dispatch = useDispatch<any>();

  const loader = useContext(LoaderContext);
  const { loggedInUser, loggedInUserRole, selectedBrand } =
    useContext(GlobalContext);

  const [msStaticData, setMSStaticData] = useState<Record<string, any>>({});
  const [locationInfo, setLocationInfo] = useState<Record<string, any>>({});
  const [briefDetail, setBriefDetail] = useState<Record<string, any> | null>(
    null
  );
  const [formError, setFormError] = useState<{ message?: string }>({});

  const fetchMSStaticData = useCallback(async () => {
    loader(true);
    try {
      const res = await morganStanleyService.getMSStaticData();
      let locRes;
      let briefRes;
      if (locID) {
        locRes = await locationService.getLocationDetails(locID);
        //briefRes = await morganStanleyService.getBriefDetails(locID);
      }
      if (locID && briefRes?.status === HTTP_STATUS.OK) {
        setBriefDetail(briefRes?.data || null);
      }
      if (res.status === HTTP_STATUS.OK) {
        setMSStaticData(res?.data || {});
      }
      if (locID && locRes?.status === HTTP_STATUS.OK) {
        setLocationInfo(locRes?.data || {});
      }
    } catch (err) {
      logger.error("fetchMSStaticData error", err);
    } finally {
      loader(false);
    }
  }, [loader, locID]);

  useEffect(() => {
    dispatch(
      getUserThunk({ ...loggedInUser, brandId: selectedBrand?.id } as any)
    );
    fetchMSStaticData();
  }, [fetchMSStaticData, dispatch, loggedInUser?.id, selectedBrand?.id]);

  const checkForMSIDAssociationWithEmail = useCallback(
    async ({ email, msid, index, setFieldError, setSubmitting }: any) => {
      try {
        if (email && msid) {
          const res = await morganStanleyService.checkValidationMsid({
            email: email || "",
            msid: msid || "",
          });
          if (res.status === HTTP_STATUS.OK && !res.data) {
            setFieldError(
              `advisorDetail[${index}].msid`,
              "This MSID already associated with another email"
            );
            setSubmitting(false);

            return false;
          }
        }
        return true;
      } catch (err) {
        logger.error("checkForMSIDAssociationWithEmail error", err);
      }
    },
    []
  );

  const handleFormSubmit = (values: any, actions: any) => {
    setFormError({});

    const uniqMSID = uniqBy(values.advisorDetail, "msid");
    const uniqEmail = uniqBy(values.advisorDetail, "email");

    if (
      values.team_or_individual === "Team" &&
      uniqMSID.length < values.advisorDetail.length
    ) {
      // msid is duplicate
      setFormError({
        message: `You have entered duplicate MSID for advisor`,
      });
      actions.setSubmitting(false);
      return;
    }
    if (
      values.team_or_individual === "Team" &&
      uniqEmail.length < values.advisorDetail.length
    ) {
      // email is duplicate
      setFormError({
        message: `You have entered duplicate email for advisor`,
      });
      actions.setSubmitting(false);
      return;
    }

    // Check for MSID association with the given Email ID
    Promise.all(
      values.advisorDetail.map(async (a, i) => {
        let isERR = await checkForMSIDAssociationWithEmail({
          email: a.email,
          msid: a.msid,
          index: i,
          setFieldError: actions.setFieldError,
          setSubmitting: actions.setSubmitting,
        });

        return isERR ? Promise.resolve(isERR) : Promise.reject(isERR);
      })
    )
      .then(() => {
        RpConfirmDialog(
          (<RpConfirm message="You want to save now?" />) as any,
          () => {
            saveBrief();
          },
          () => {
            actions.setSubmitting(false);
            return;
          }
        );
      })
      .catch(() => {
        setFormError({
          message: `There are some errors. Please re-check the form`,
        });
      });

    async function saveBrief() {
      loader(true);
      try {
        const packageInfo = MSPackagesData.find((p) => p.tier === values?.tier);

        const reqData = {
          fk_brand_id: 1,
          system_generated_brand_id: "BD919943",
          brand_name: selectedBrand?.brand_name,
          ...values,
          amount: packageInfo?.price || "",
          extra_charge: 0,
          description: packageInfo?.description || "",
          is_recommended: packageInfo?.recommended || false,
          locationId: locID || "",
        };

        const res = await morganStanleyService.saveActionBrief(reqData);

        if (res.status === HTTP_STATUS.OK) {
          grantThankYouAccessForFlow({
            stateFrom: "ms_activation_brief",
          });
          navigate(ROUTES.THANK_YOU, {
            replace: true,
            state: {
              from: "ms_activation_brief",
            },
          });
        }
      } catch (err) {
        logger.error("saveBrief error", err);
      } finally {
        loader(false);
      }
    }
  };

  const initialValues = useMemo(() => {
    let initialFormData: any = {
      team_or_individual: "Individual",
      advisorDetail: [
        {
          first_name: "",
          last_name: "",
          email: "",
          msid: "",
        },
      ],
      approved_team_name: "",
      functional_title: "",
      line_of_business: "",
      area_of_focus: [],
      other_area_of_focus: "",
      street_in_your_area: "",
      state_registered_in: [],
      street_address: "",
      city: "",
      state: "",
      zip_code: "",
      phone: "",
      radius: "",
      landing_page_url: "",
      email_list: [],
      tier: MSPackagesData?.[0]?.tier || "",
      duration: "12",
    };

    if (briefDetail) {
      // get saved brief
      initialFormData = {
        ...initialFormData,
        ...briefDetail,
      };

      return initialFormData;
    } else if (loggedInUserRole === USER_ROLE.PARTICIPANT.NAME) {
      // when participant
      const {
        email,
        first_name,
        last_name,
        participantData,
        // partLocationData,
      } = loggedInUser as any;
      const brand_define_participant_id =
        participantData?.brand_define_participant_id || "";

      initialFormData["advisorDetail"] = [
        {
          first_name: first_name || "",
          last_name: last_name || "",
          email: email || "",
          msid: brand_define_participant_id || "",
        },
      ];
    } else {
      const { participantData } = locationInfo as any;

      if (participantData?.[0]) {
        const { user_email, msid, first_name, last_name } =
          participantData?.[0] || {};
        initialFormData["advisorDetail"] = [
          {
            first_name: first_name || "",
            last_name: last_name || "",
            email: user_email || "",
            msid: msid || "",
          },
        ];
      }
    }

    const {
      location_name,
      street_address,
      landing_page_url,
      radius,
      city,
      zip_code,
    } = locationInfo;

    initialFormData.approved_team_name = location_name || "";
    initialFormData.street_address = street_address || "";
    initialFormData.landing_page_url = landing_page_url || "";
    initialFormData.radius = radius || "";
    initialFormData.city = city || "";
    initialFormData.zip_code = zip_code || "";

    return initialFormData;
  }, [loggedInUser, loggedInUserRole, locationInfo, briefDetail]);

  const shouldDisableMSID = useMemo(() => {
    if (loggedInUserRole === USER_ROLE.PARTICIPANT.NAME) {
      const { participantData } = loggedInUser as any;
      const brand_define_participant_id =
        participantData?.brand_define_participant_id || "";
      return brand_define_participant_id ? true : false;
    } else {
      const { participantData } = locationInfo as any;

      if (participantData?.[0]) {
        const { msid } = participantData?.[0] || {};
        return msid ? true : false;
      }
      return false;
    }
  }, [locationInfo, loggedInUserRole, loggedInUser]);

  return (
    <div className="ms-activation-brief">
      <div className="text-center my-4">
        <h2 className="main-heading">
          1. Activation Brief for Financial Advisor Participation
        </h2>
        <div className="description">
          <p>
            Name will be used as entered for public-facing advertisements, so
            keep spelling and capitalization consistent. The website address
            will be the URL that all traffic is sent to.
          </p>
          <p>
            Team Name must be complete, approved name, including “at Morgan
            Stanley”
          </p>
        </div>
      </div>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={MSActivationBriefFormValidationSchema}
        enableReinitialize
      >
        <MSActivationBriefForm
          msStaticData={msStaticData}
          isNewSubmission={locID}
          shouldDisableMSID={shouldDisableMSID}
        />
      </Formik>
      {!isEmpty(formError) && <RpSnackbar show message={formError.message} />}
    </div>
  );
}
